import { Badge } from '@/shared/ui/badge'
import { t, type TranslationKey } from '@/shared/lib/i18n'
import type WebInstrumentTaskReport from '@/shared/types/web/WebInstrumentTaskReport'

interface Props {
  record: Pick<WebInstrumentTaskReport, 'statusCode' | 'statusLabel' | 'isSigned'> & {
    isRejected?: boolean
    derivedStatus?: { code: string | null; live: boolean; signable: boolean }
  }
}

const STATUS_MAP: Record<
  string,
  { variant: 'outline' | 'destructive' | 'default' | 'secondary'; labelKey: TranslationKey }
> = {
  OPEN: { variant: 'outline', labelKey: 'reports.monthly.statusOpen' },
  PENDING: { variant: 'secondary', labelKey: 'reports.monthly.statusPending' },
  SIGNED: { variant: 'default', labelKey: 'reports.monthly.statusSigned' },
}

// Derived live statuses (REBUILD-141) only apply while a report is OPEN. Once a
// report is PENDING/SIGNED the persisted status is the frozen source of truth.
// Codes here MUST mirror Reports::ReportStatusResolverService (IN_PROGRESS /
// ACTION_NEEDED / READY_TO_SIGN) — that Ruby service is the source of truth.
const DERIVED_STATUS_MAP: Record<
  string,
  { variant: 'outline' | 'destructive' | 'default' | 'secondary'; labelKey: TranslationKey }
> = {
  IN_PROGRESS: { variant: 'outline', labelKey: 'reports.monthly.derivedStatusInProgress' },
  ACTION_NEEDED: { variant: 'destructive', labelKey: 'reports.monthly.derivedStatusActionNeeded' },
  READY_TO_SIGN: { variant: 'default', labelKey: 'reports.monthly.derivedStatusReadyToSign' },
}

const ReportStatusBadge = ({ record }: Props) => {
  const code = record.statusCode ?? ''

  if (code === 'OPEN' && record.isRejected) {
    return <Badge variant="destructive">{t('reports.monthly.statusRejected')}</Badge>
  }

  const derivedCode = record.derivedStatus?.code ?? ''
  const derivedMapping = DERIVED_STATUS_MAP[derivedCode]
  if (derivedMapping) {
    return <Badge variant={derivedMapping.variant}>{t(derivedMapping.labelKey)}</Badge>
  }

  const mapping = STATUS_MAP[code]

  if (mapping) {
    return <Badge variant={mapping.variant}>{t(mapping.labelKey as TranslationKey)}</Badge>
  }

  return <Badge variant="secondary">{record.statusLabel ?? code}</Badge>
}

export default ReportStatusBadge
